import { useCallback, useRef, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'

type ResizableThProps = {
  columnId: string
  label?: string
  width?: number
  minWidth?: number
  className?: string
  children?: ReactNode
  onResize: (columnId: string, width: number) => void
}

export const ResizableTh = ({
  columnId,
  label,
  width,
  minWidth = 56,
  className,
  children,
  onResize,
}: ResizableThProps) => {
  const thRef = useRef<HTMLTableCellElement>(null)
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null)

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLSpanElement>) => {
      event.preventDefault()
      event.stopPropagation()
      const th = thRef.current
      if (!th) {
        return
      }
      const startWidth = width ?? th.getBoundingClientRect().width
      dragRef.current = { startX: event.clientX, startWidth }
      event.currentTarget.setPointerCapture(event.pointerId)

      const handleMove = (moveEvent: PointerEvent) => {
        const drag = dragRef.current
        if (!drag) {
          return
        }
        const next = Math.max(minWidth, drag.startWidth + (moveEvent.clientX - drag.startX))
        onResize(columnId, next)
      }

      const handleUp = (upEvent: PointerEvent) => {
        dragRef.current = null
        window.removeEventListener('pointermove', handleMove)
        window.removeEventListener('pointerup', handleUp)
        try {
          event.currentTarget.releasePointerCapture(upEvent.pointerId)
        } catch {
          // already released
        }
      }

      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleUp)
    },
    [columnId, minWidth, onResize, width],
  )

  const content = children !== undefined ? children : label

  return (
    <th
      ref={thRef}
      className={['market-table__th--resizable', className].filter(Boolean).join(' ')}
      style={width != null ? { width, minWidth: Math.min(minWidth, width) } : { minWidth }}
      scope="col"
      aria-label={!content && label ? label : undefined}
    >
      {content}
      <span
        className="market-table__resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label={label ? `Изменить ширину столбца «${label}»` : 'Изменить ширину столбца'}
        onPointerDown={handlePointerDown}
      />
    </th>
  )
}
